import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Download,
  Globe2,
  Loader2,
  Plus,
  RefreshCw,
  Save,
  Search,
  Share2,
  Trash2,
  Upload,
} from 'lucide-react';
import { useEditor } from '../../state/store';
import { useOptionalAuth } from '../../auth/AuthContext';
import { ApiError, formatDate } from '../../auth/api';
import { PublishTemplateDialog } from '../../auth/PublishTemplateDialog';
import { TEMPLATES, Template } from '../../templates/library';
import {
  UserTemplate,
  exportUserTemplates,
  importUserTemplates,
  listUserTemplates,
  removeUserTemplate,
  saveUserTemplate,
  templateGrid,
  templatePreview,
} from '../../templates/userTemplates';
import {
  PublicTemplateSummary,
  deletePublicTemplate,
  getPublicTemplate,
  listPublicTemplates,
} from '../../templates/publicTemplates';
import { CellWrite } from '../../tools/types';
import { TRANSPARENT } from '../../core/types';
import { downloadText, pickFile, readFileText } from '../fileIO';
import { Hint, Panel } from '../widgets/common';

const PUBLIC_PAGE = 8;

function messageFor(caught: unknown, fallback: string): string {
  return caught instanceof ApiError || caught instanceof Error ? caught.message : fallback;
}

/**
 * Template browser.
 *
 * Built-in layouts, the user's own saved designs and the shared gallery all
 * insert the same way. Saving keeps colours and attributes, not just the
 * characters, so a saved template comes back looking like what was designed.
 * The public section only exists on the hosted edition; the offline desktop
 * build has no account and therefore no gallery.
 */
export function TemplatesPanel(): JSX.Element {
  const cursor = useEditor((s) => s.cursor);
  const revision = useEditor((s) => s.revision);
  const auth = useOptionalAuth();
  const viewerId = auth?.user?.id ?? null;
  const isAdmin = auth?.user?.role === 'admin';

  const [mine, setMine] = useState<UserTemplate[]>(() => listUserTemplates());
  const [name, setName] = useState('');

  const [publicList, setPublicList] = useState<PublicTemplateSummary[]>([]);
  const [publicTotal, setPublicTotal] = useState(0);
  const [publicQuery, setPublicQuery] = useState('');
  const [publicLoading, setPublicLoading] = useState(false);
  const [publicMore, setPublicMore] = useState(false);
  const [publicError, setPublicError] = useState<string | null>(null);
  const [publicVersion, setPublicVersion] = useState(0);
  const [usingPublic, setUsingPublic] = useState<string | null>(null);
  const [deleteArmed, setDeleteArmed] = useState<string | null>(null);
  const [deletingPublic, setDeletingPublic] = useState<string | null>(null);
  const [publishTarget, setPublishTarget] = useState<UserTemplate | null>(null);
  const publicRequest = useRef(0);

  const refresh = useCallback(() => setMine(listUserTemplates()), []);

  useEffect(() => {
    if (viewerId === null) {
      setPublicList([]);
      setPublicTotal(0);
      setPublicError(null);
      return;
    }

    const request = ++publicRequest.current;
    const timer = window.setTimeout(
      () => {
        setPublicLoading(true);
        setPublicError(null);
        void listPublicTemplates({ query: publicQuery, offset: 0, limit: PUBLIC_PAGE })
          .then((page) => {
            if (request !== publicRequest.current) return;
            setPublicList(page.templates);
            setPublicTotal(page.total);
          })
          .catch((caught: unknown) => {
            if (request !== publicRequest.current) return;
            setPublicError(messageFor(caught, 'Could not load public templates.'));
          })
          .finally(() => {
            if (request === publicRequest.current) setPublicLoading(false);
          });
      },
      publicQuery ? 250 : 0,
    );

    return () => window.clearTimeout(timer);
  }, [viewerId, publicQuery, publicVersion]);

  const loadMorePublic = async () => {
    if (publicLoading || publicMore) return;
    const request = publicRequest.current;
    setPublicMore(true);
    setPublicError(null);
    try {
      const page = await listPublicTemplates({
        query: publicQuery,
        offset: publicList.length,
        limit: PUBLIC_PAGE,
      });
      if (request !== publicRequest.current) return;
      setPublicList((current) => {
        const seen = new Set(current.map((template) => template.id));
        return [...current, ...page.templates.filter((template) => !seen.has(template.id))];
      });
      setPublicTotal(page.total);
    } catch (caught) {
      if (request === publicRequest.current) {
        setPublicError(messageFor(caught, 'Could not load more templates.'));
      }
    } finally {
      setPublicMore(false);
    }
  };

  const insertBuiltIn = (template: Template) => {
    const store = useEditor.getState();
    const origin = cursor ?? { x: 0, y: 0 };
    const brush = store.brush;
    const writes: CellWrite[] = [];
    template.art.split('\n').forEach((line, y) => {
      [...line].forEach((ch, x) => {
        if (ch === ' ') return;
        writes.push({
          x: origin.x + x,
          y: origin.y + y,
          cell: { char: ch, fg: brush.fg, bg: brush.bg, attrs: brush.attrs },
        });
      });
    });
    store.writeCells(writes, `Insert ${template.name}`);
    template.variables.forEach((v) => {
      const current = useEditor.getState();
      if (!current.active().doc.variables.some((e) => e.name === v.name)) current.upsertVariable(v);
      if (v.previewValue !== undefined) current.setPreviewValue(v.name, v.previewValue);
    });
    useEditor.getState().notify(`Inserted "${template.name}".`, 'success');
  };

  const insertMine = (template: UserTemplate) => {
    const store = useEditor.getState();
    const origin = cursor ?? { x: 0, y: 0 };
    const grid = templateGrid(template);
    const writes: CellWrite[] = [];
    for (let y = 0; y < grid.height; y++) {
      for (let x = 0; x < grid.width; x++) {
        const cell = grid.get(x, y);
        if (cell.char === '' && cell.bg === TRANSPARENT && cell.fg === TRANSPARENT) continue;
        writes.push({ x: origin.x + x, y: origin.y + y, cell });
      }
    }
    store.writeCells(writes, `Insert ${template.name}`);
    template.variables.forEach((v) => {
      const current = useEditor.getState();
      if (!current.active().doc.variables.some((e) => e.name === v.name)) current.upsertVariable(v);
    });
    store.notify(`Inserted "${template.name}".`, 'success');
  };

  const insertPublic = async (summary: PublicTemplateSummary) => {
    if (usingPublic) return;
    setUsingPublic(summary.id);
    setPublicError(null);
    try {
      const { template } = await getPublicTemplate(summary.id);
      insertMine(template);
    } catch (caught) {
      setPublicError(messageFor(caught, 'Could not insert that template.'));
    } finally {
      setUsingPublic(null);
    }
  };

  const removePublic = async (template: PublicTemplateSummary) => {
    if (deletingPublic) return;
    if (deleteArmed !== template.id) {
      setDeleteArmed(template.id);
      window.setTimeout(() => {
        setDeleteArmed((current) => (current === template.id ? null : current));
      }, 4000);
      return;
    }

    setDeletingPublic(template.id);
    setDeleteArmed(null);
    setPublicError(null);
    try {
      await deletePublicTemplate(template.id);
      setPublicList((current) => current.filter((item) => item.id !== template.id));
      setPublicTotal((current) => Math.max(0, current - 1));
      useEditor.getState().notify(`Removed "${template.name}" from public templates.`, 'success');
    } catch (caught) {
      setPublicError(messageFor(caught, 'Could not remove that template.'));
    } finally {
      setDeletingPublic(null);
    }
  };

  const save = (fromSelection: boolean) => {
    const store = useEditor.getState();
    const tab = store.active();
    const bounds = tab.selection.getBounds();
    if (fromSelection && !bounds) {
      store.notify('Select an area first, or save the whole design.', 'warn');
      return;
    }
    const source = tab.doc.composite();
    const grid = fromSelection && bounds ? source.extract(bounds) : source;
    const saved = saveUserTemplate({
      name: name.trim() || tab.doc.name,
      grid,
      variables: tab.doc.variables,
    });
    if (!saved) {
      store.notify('Could not save — browser storage is unavailable or full.', 'error');
      return;
    }
    setName('');
    refresh();
    store.notify(`Saved template "${saved.name}" (${saved.width}×${saved.height}).`, 'success');
  };

  const categories = [...new Set(TEMPLATES.map((t) => t.category))];

  return (
    <>
      <Panel title="Save current design" collapsible={false}>
        <input
          className="input"
          placeholder="Template name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="grid2" style={{ marginTop: 7 }}>
          <button className="btn solid" onClick={() => save(true)} title="Save just the selected area">
            <Save size={14} /> Selection
          </button>
          <button className="btn solid" onClick={() => save(false)} title="Save the whole canvas">
            <Save size={14} /> Whole design
          </button>
        </div>
        <Hint>
          Saved in this browser. Use Export to move them to another machine
          {auth?.user ? '; use the share icon on a saved template to publish it.' : '.'}
        </Hint>
      </Panel>

      <Panel title={`My templates (${mine.length})`} key={`mine-${revision}`}>
        {mine.length === 0 ? (
          <div className="empty-state">
            <span>Nothing saved yet.</span>
            <span className="faint">Design something, name it above, and hit Save.</span>
          </div>
        ) : (
          <div className="list" style={{ maxHeight: 300 }}>
            {mine.map((t) => (
              <div key={t.id} className="card" onClick={() => insertMine(t)} title="Click to insert">
                <div className="row">
                  <b style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</b>
                  <span className="tag">
                    {t.width}×{t.height}
                  </span>
                  {auth?.user ? (
                    <button
                      className="icon-btn"
                      title="Publish this template to the shared gallery"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPublishTarget(t);
                      }}
                    >
                      <Share2 size={13} />
                    </button>
                  ) : null}
                  <button
                    className="icon-btn"
                    title="Delete template"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeUserTemplate(t.id);
                      refresh();
                    }}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
                <pre>{templatePreview(t, 8)}</pre>
              </div>
            ))}
          </div>
        )}
        <div className="grid2" style={{ marginTop: 8 }}>
          <button
            className="btn solid"
            onClick={() => downloadText('pixansi-templates.json', exportUserTemplates(), 'application/json')}
            title="Download all saved templates"
          >
            <Download size={14} /> Export
          </button>
          <button
            className="btn solid"
            onClick={async () => {
              const file = await pickFile('.json,application/json');
              if (!file) return;
              const n = importUserTemplates(await readFileText(file));
              refresh();
              useEditor
                .getState()
                .notify(n > 0 ? `Imported ${n} template(s).` : 'No templates found in that file.', n > 0 ? 'success' : 'warn');
            }}
          >
            <Upload size={14} /> Import
          </button>
        </div>
      </Panel>

      {auth?.user ? (
        <Panel
          title={`Public templates${publicTotal ? ` (${publicTotal})` : ''}`}
          actions={
            <button
              className="icon-btn"
              title="Refresh public templates"
              onClick={() => setPublicVersion((value) => value + 1)}
            >
              {publicLoading ? <Loader2 size={13} className="spin" /> : <RefreshCw size={13} />}
            </button>
          }
        >
          <div className="row" style={{ marginBottom: 7 }}>
            <Search size={13} className="faint" />
            <input
              className="input"
              placeholder="Search public templates"
              value={publicQuery}
              onChange={(event) => setPublicQuery(event.target.value)}
              aria-label="Search public templates"
            />
          </div>

          {publicError ? <div className="form-note error">{publicError}</div> : null}

          {publicLoading && publicList.length === 0 ? (
            <div className="empty-state">
              <Loader2 size={16} className="spin" />
              <span>Loading public templates…</span>
            </div>
          ) : publicList.length === 0 ? (
            <div className="empty-state">
              <Globe2 size={16} />
              <span>{publicQuery ? 'No templates match that search.' : 'No public templates yet.'}</span>
              <span className="faint">
                {publicQuery
                  ? 'Try a different name, description or author.'
                  : 'Save a design, then press the share icon on it.'}
              </span>
            </div>
          ) : (
            <div className="list" style={{ maxHeight: 430 }}>
              {publicList.map((t) => {
                const canRemove = t.mine || isAdmin;
                const armed = deleteArmed === t.id;
                return (
                  <div
                    key={t.id}
                    className="card"
                    onClick={() => void insertPublic(t)}
                    title={`Insert "${t.name}" by ${t.author}`}
                    aria-busy={usingPublic === t.id}
                  >
                    <div className="row">
                      <b style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {t.name}
                      </b>
                      {usingPublic === t.id ? <Loader2 size={13} className="spin" /> : null}
                      <span className="tag">
                        {t.width}×{t.height}
                      </span>
                      {canRemove ? (
                        <button
                          className="icon-btn"
                          title={
                            armed
                              ? 'Click again to remove'
                              : isAdmin && !t.mine
                                ? 'Remove template (administrator)'
                                : 'Remove your public template'
                          }
                          style={armed ? { color: 'var(--error)' } : undefined}
                          disabled={deletingPublic === t.id}
                          onClick={(event) => {
                            event.stopPropagation();
                            void removePublic(t);
                          }}
                        >
                          {deletingPublic === t.id ? (
                            <Loader2 size={13} className="spin" />
                          ) : (
                            <Trash2 size={13} />
                          )}
                        </button>
                      ) : null}
                    </div>
                    <div className="row faint" style={{ fontSize: 10.5, marginTop: 2 }}>
                      <span>by {t.author}</span>
                      <span className="spacer" />
                      <span>{formatDate(t.createdAt)}</span>
                    </div>
                    <pre>{t.preview || ' '}</pre>
                  </div>
                );
              })}
              {publicList.length < publicTotal ? (
                <button
                  className="btn solid"
                  style={{ marginTop: 5 }}
                  onClick={() => void loadMorePublic()}
                  disabled={publicMore}
                >
                  {publicMore ? <Loader2 size={13} className="spin" /> : <Plus size={13} />}
                  Load more
                </button>
              ) : null}
            </div>
          )}

          <Hint>
            Shared by members. Every template is server-validated, stored as inert
            data, and can be removed by its author or an administrator.
          </Hint>
        </Panel>
      ) : null}

      {categories.map((cat) => (
        <Panel key={cat} title={cat} defaultOpen={cat === 'dashboard'}>
          <div className="group" style={{ gap: 5 }}>
            {TEMPLATES.filter((t) => t.category === cat).map((t) => (
              <button
                key={t.id}
                className="option"
                onClick={() => insertBuiltIn(t)}
                title={`${t.description} — inserts at the cursor`}
              >
                <Plus size={14} />
                <span className="label">{t.name}</span>
                <span className="meta">
                  {t.width}×{t.height}
                </span>
              </button>
            ))}
          </div>
        </Panel>
      ))}

      <Hint>
        Templates insert at the cursor position on the canvas
        {cursor ? ` (currently ${cursor.x}, ${cursor.y})` : ''}.
      </Hint>

      {publishTarget ? (
        <PublishTemplateDialog
          template={publishTarget}
          onClose={() => setPublishTarget(null)}
          onPublished={() => setPublicVersion((value) => value + 1)}
        />
      ) : null}
    </>
  );
}
